import type { ICampaignTemplate } from '~/modules/campaign-template/types';
import type { IInitializeCampaignGroup } from '~/modules/campaign-group';
import type { ICampaignGroup } from '~/modules/campaign-group/types';
import type { IInitializeCGUpdater } from '../hooks';
import type { ReactNode } from 'react';

import { useCGTemplateCheckState, useInitializeCGTemplateUpdater } from '../hooks';
import { useInitializeCampaignGroup } from '~/modules/campaign-group';
import { createContext, useContext, useMemo } from 'react';

export interface ICGTemplateContext extends IInitializeCampaignGroup, IInitializeCGUpdater {
  isShowDiscard: boolean;
  isDisabledEditing: boolean;
  isDisableAddTemplates: boolean;
  /**
   * Not including campaign `deleted`
   * @note Don't use for campaign analytics
   */
  campaignGroupCampaigns: ICampaignTemplate[];
}
export interface ICGTemplateProvider {
  children: ReactNode;
  campaignGroupId?: ICampaignGroup['id'];
  campaignGroup?: ICampaignGroup;
}

export const CGTemplateContext = createContext<ICGTemplateContext | undefined>(undefined);

export const CGTemplateProvider: React.FC<ICGTemplateProvider> = ({
  children,
  campaignGroup,
  campaignGroupId,
}) => {
  const initializeCampaignGroup = useInitializeCampaignGroup({ campaignGroupId, campaignGroup });

  const { isShowDiscard, isDisabledEditing, isDisableAddTemplates } = useCGTemplateCheckState(
    initializeCampaignGroup.campaignGroup,
  );

  const initializeCGUpdater = useInitializeCGTemplateUpdater({
    initializeCampaignGroup,
    isShowDiscard: !!isShowDiscard,
  });

  const contextValue = useMemo(
    () => ({
      ...initializeCGUpdater,
      ...initializeCampaignGroup,
      isShowDiscard,
      isDisabledEditing,
      isDisableAddTemplates,
    }),
    [
      isShowDiscard,
      isDisabledEditing,
      initializeCGUpdater,
      initializeCampaignGroup,
      isDisableAddTemplates,
    ],
  );

  return <CGTemplateContext.Provider value={contextValue}>{children}</CGTemplateContext.Provider>;
};

export const useCGTemplateContext = () => {
  const context = useContext(CGTemplateContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
